import React, { ReactElement, useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import useTitleBarSelect from '../../../../hooks/useTitleBarSelect';
import { SyntheticRiskContent } from '../synthetic-risk/SyntheticRisk';
import {
  APPEND_NEW_SELECT_TO_TITLEBAR,
  REMOVE_SELECT_FROM_TITLEBAR,
} from '../../../../types/redux/pages/pageTypes';
import {
  addAdditionalTitleBarComponents,
  removeTitleBarComponent,
  toggleTitleBarLoading,
} from '../../../../redux/pages/actions';
import { Select } from '../../../../types/redux/pages/PagesStore';
import useRouteParams from '../../../../hooks/useRouteParams';
import { FundInfoComponentProps } from '../../../layout/general/SimpleGeneralPageWrapper';
import { QueryBoundary } from '../../../utilityComponents/QueryBoundary';
import { useSrriShareClassPageQuery } from '../../../../api/rs-hg/risk/hooks/queries';
import type { SrriShareClassPagePayload } from '../../../../api/rs-hg/risk/srriShareClassPage.types';

interface SyntheticRiskShareClassViewProps {
  fundId: string;
}

const SyntheticRiskShareClassView = (
  props: SyntheticRiskShareClassViewProps,
): ReactElement => {
  const { fundId } = props;
  const shareClass = useTitleBarSelect('shareClassSelection');

  return (
    <SyntheticRiskContent
      fundId={fundId}
      gmidShareclass={shareClass ?? undefined}
    />
  );
};

const SyntheticRiskShareClass = (props: FundInfoComponentProps) => {
  const { fundId } = props;
  const shareClassRoute = useRouteParams('shareClass');
  const shareClassInfoQuery = useSrriShareClassPageQuery(fundId);
  const dispatch = useDispatch();

  const shareClassOptions = useMemo(
    () =>
      shareClassInfoQuery.data?.data?.share_classes?.map((item) => ({
        label: item.share_class_name,
        value: item.share_class_old_gm_id,
      })) ?? [],
    [shareClassInfoQuery.data],
  );

  useEffect(() => {
    if (!shareClassOptions.length) {
      return;
    }

    dispatch(toggleTitleBarLoading(true));

    const selectsToAdd: Select[] = [
      {
        titleBarTitle: 'Share Class',
        titleBarKey: 'shareClassSelection',
        values: shareClassOptions,
        displayOnOverviewPage: false,
        aValueIsAlwaysSelected: true,
        currentValue: shareClassRoute.shareClass
          ? shareClassRoute.shareClass
          : shareClassOptions[0].value,
      },
    ];

    dispatch(
      addAdditionalTitleBarComponents(
        selectsToAdd,
        APPEND_NEW_SELECT_TO_TITLEBAR,
      ),
    );
    dispatch(toggleTitleBarLoading(false));

    return () => {
      dispatch(
        removeTitleBarComponent(
          'shareClassSelection',
          REMOVE_SELECT_FROM_TITLEBAR,
        ),
      );
    };
  }, [shareClassOptions, dispatch, shareClassRoute.shareClass]);

  return (
    <QueryBoundary<SrriShareClassPagePayload>
      isLoading={shareClassInfoQuery.isLoading}
      isFetching={shareClassInfoQuery.isFetching}
      isError={shareClassInfoQuery.isError}
      error={shareClassInfoQuery.error}
      data={shareClassInfoQuery.data?.data}
      loadingMessages={['Loading Share Class Data...']}
      noDataMessage="No share class data available."
      errorMessage="No share class data available."
    >
      {() => <SyntheticRiskShareClassView fundId={fundId} />}
    </QueryBoundary>
  );
};

export default SyntheticRiskShareClass;
